import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useId } from 'react';
import styled from 'styled-components';

import type { ModelInfo } from '@paradigm/terminal-core';

import { normalizeModelId } from '../../models/model-labels';
import {
  formatVersionLabel,
  groupByModelAndVersion,
  groupModelsByCompany,
  type ModelVersionGroup,
} from '../../models/provider-groups';
import { THIN_SCROLLBAR } from '../../styles/scrollbar';

interface Props {
  readonly models: readonly ModelInfo[];
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}

export default function ModelPicker({ models, selectedId, onSelect }: Props) {
  const menuId = useId();
  if (models.length === 0) return null;

  const selected =
    (selectedId != null
      ? (models.find((m) => m.id === selectedId) ??
        models.find(
          (m) => normalizeModelId(m.id) === normalizeModelId(selectedId),
        ))
      : undefined) ?? models[0];
  if (selected == null) return null;

  const groups = groupModelsByCompany(models);

  return (
    <DropdownMenuPrimitive.Root>
      <Trigger aria-label={`Model: ${selected.label}`}>
        <TriggerLabel>{selected.label}</TriggerLabel>
      </Trigger>
      <DropdownMenuPrimitive.Portal>
        <Content
          sideOffset={6}
          align="end"
          side="top"
          onCloseAutoFocus={(e) => {
            // Prevent Radix from returning focus to the trigger button —
            // redirect it to the chat textarea so the user can type
            // immediately after picking a model.
            e.preventDefault();
            const textarea = document.querySelector<HTMLTextAreaElement>(
              'textarea[data-chat-input]',
            );
            textarea?.focus();
          }}
        >
          {groups.map((group, gi) => {
            const headingId = `${menuId}-group-${gi}`;
            const versionGroups = groupByModelAndVersion(group.models);
            return (
              <Group key={group.label} aria-labelledby={headingId}>
                <Heading id={headingId}>{group.label}</Heading>
                {versionGroups.map((vg) => (
                  <ModelEntry
                    key={vg.modelName}
                    vg={vg}
                    selectedId={selected.id}
                    onSelect={onSelect}
                  />
                ))}
              </Group>
            );
          })}
        </Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

/** A single model entry — flat item or a Radix Sub (flyout) for versions. */
function ModelEntry({
  vg,
  selectedId,
  onSelect,
}: {
  readonly vg: ModelVersionGroup;
  readonly selectedId: string;
  readonly onSelect: (id: string) => void;
}) {
  // Versions arrive pre-sorted (newest first, legacy last) from
  // groupByModelAndVersion.
  const sorted = vg.versions;

  if (sorted.length === 1) {
    const v = sorted[0]!;
    const label = formatVersionLabel(v.parsed);
    return (
      <Item onSelect={() => onSelect(v.id)}>
        <ItemLabel>
          {label}
          {v.legacy === true && <LegacyTag>Legacy</LegacyTag>}
        </ItemLabel>
        <CheckSlot>{v.id === selectedId && <CheckIcon />}</CheckSlot>
      </Item>
    );
  }

  // Multiple entries → Radix Sub (flyout extending right).
  const anySelected = sorted.some((v) => v.id === selectedId);
  return (
    <DropdownMenuPrimitive.Sub>
      <SubTrigger>
        <ItemLabel>
          {vg.modelName}
          {anySelected && <SelectedDot />}
        </ItemLabel>
        <CaretSlot>
          <CaretIcon />
        </CaretSlot>
      </SubTrigger>
      <DropdownMenuPrimitive.Portal>
        <SubContent sideOffset={4}>
          {sorted.map((v) => {
            const label = formatVersionLabel(v.parsed);
            return (
              <Item key={v.id} onSelect={() => onSelect(v.id)}>
                <ItemLabel>
                  {label}
                  {v.legacy === true && <LegacyTag>Legacy</LegacyTag>}
                  {vg.hasMultipleProviders && (
                    <ProviderTag>{v.parsed.provider}</ProviderTag>
                  )}
                </ItemLabel>
                <CheckSlot>{v.id === selectedId && <CheckIcon />}</CheckSlot>
              </Item>
            );
          })}
        </SubContent>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Sub>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <path
        d="M5 12L10 17L20 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CaretIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
      <path
        d="M9 6L15 12L9 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Styled Components ──────────────────────────────────────────────────

const Trigger = styled(DropdownMenuPrimitive.Trigger)`
  all: unset;
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--text-label);
  cursor: pointer;

  &:hover,
  &[data-state='open'] {
    color: var(--text-primary);
    background-color: var(--hover-bg);
  }
`;

const TriggerLabel = styled.span`
  white-space: nowrap;
`;

const Content = styled(DropdownMenuPrimitive.Content)`
  min-width: 200px;
  max-height: var(--radix-dropdown-menu-content-available-height);
  overflow-y: auto;
  overscroll-behavior: none;
  padding: 6px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-color-container);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.32);
  z-index: 2000;
  ${THIN_SCROLLBAR}
`;

const SubContent = styled(DropdownMenuPrimitive.SubContent)`
  min-width: 160px;
  max-height: 300px;
  overflow-y: auto;
  overscroll-behavior: none;
  padding: 6px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-color-container);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.32);
  z-index: 2001;
  ${THIN_SCROLLBAR}
`;

const Group = styled(DropdownMenuPrimitive.Group)`
  & + & {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid var(--border-color);
  }
`;

const Heading = styled(DropdownMenuPrimitive.Label)`
  padding: 6px 8px 4px;
  font-size: 0.7rem;
  color: var(--text-label);
`;

const Item = styled(DropdownMenuPrimitive.Item)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px;
  align-items: center;
  gap: 8px;
  height: 28px;
  padding: 0 8px 0 16px;
  border-radius: 4px;
  font-size: 0.8rem;
  color: var(--text-primary);
  cursor: pointer;
  outline: none;
  user-select: none;

  &[data-highlighted],
  &:hover {
    background-color: var(--hover-bg);
  }
`;

const SubTrigger = styled(DropdownMenuPrimitive.SubTrigger)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px;
  align-items: center;
  gap: 8px;
  height: 28px;
  padding: 0 8px 0 16px;
  border-radius: 4px;
  font-size: 0.8rem;
  color: var(--text-primary);
  cursor: pointer;
  outline: none;
  user-select: none;

  &[data-highlighted],
  &[data-state='open'],
  &:hover {
    background-color: var(--hover-bg);
  }
`;

const ItemLabel = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LegacyTag = styled.span`
  font-size: 0.7rem;
  color: var(--text-label);
`;

const ProviderTag = styled.span`
  font-size: 0.65rem;
  padding: 0 4px;
  border-radius: 3px;
  background: var(--hover-bg);
  color: var(--text-label);
`;

const CheckSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
`;

const CaretSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-label);
`;

const SelectedDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--text-primary);
  flex-shrink: 0;
`;
